/**
 * Gespreksaanvragen — Zod-schemas, DTO's en UI-labels.
 *
 * Spec: docs/specs/01-medewerkerportaal-ui-verbeteringen.md §3 (PR-M).
 *
 * Medewerker kan een regulier HR-gesprek aanvragen (persoonlijk, salaris,
 * beoordeling of vertrouwenspersoon). Aparte feature; NIET hetzelfde als
 * ReintegratieStap (verzuim-traject).
 *
 * @deprecated Vervangen door `contact-requests.ts` (Spec C
 * contactverzoeken-module). Dit module-bestand blijft staan voor
 * backward-compatibility met bestaande routes/UI tijdens de Track C
 * migratie. Nieuwe code MOET `@streep/types` ContactRequest-DTOs gebruiken.
 */

import { z } from 'zod'
import { idParam } from './id-validators'

// ─── Enums ───────────────────────────────────────────────────────────────────

export const GespreksTypeEnum = z.enum([
  'PERSOONLIJK',
  'SALARIS',
  'BEOORDELING',
  'VERTROUWENSPERSOON',
])
export type GespreksType = z.infer<typeof GespreksTypeEnum>

export const GespreksPartnerEnum = z.enum([
  'TEAMLEIDER',
  'HR',
  'DIRECTIE',
  'VERTROUWENSPERSOON',
])
export type GespreksPartner = z.infer<typeof GespreksPartnerEnum>

export const GespreksStatusEnum = z.enum([
  'AANGEVRAAGD',
  'INGEPLAND',
  'AFGEROND',
  'GEANNULEERD',
])
export type GespreksStatus = z.infer<typeof GespreksStatusEnum>

// ─── Input schemas ───────────────────────────────────────────────────────────

export const CreateGespreksAanvraagSchema = z.object({
  type: GespreksTypeEnum,
  partner: GespreksPartnerEnum,
  toelichting: z.string().trim().max(1000).optional(),
  // Optionele directe koppeling aan een gesprekspartner (bv. een specifieke
  // vertrouwenspersoon). Wanneer aanwezig, gebruikt PR-B's `gesprek.recipient`
  // resolver UITSLUITEND deze user voor de notificatie (privacy: geen fan-out
  // naar HR/TL). Bij partner != VERTROUWENSPERSOON wordt deze waarde door
  // de UI niet meegestuurd.
  partnerUserId: idParam().optional(),
})
export type CreateGespreksAanvraagInput = z.infer<typeof CreateGespreksAanvraagSchema>

export const InplannenGespreksAanvraagSchema = z.object({
  geplandOp: z.string().datetime(),
  locatie: z.string().trim().max(200).optional(),
  notitiesAdmin: z.string().trim().max(2000).optional(),
})
export type InplannenGespreksAanvraagInput = z.infer<typeof InplannenGespreksAanvraagSchema>

export const AfrondenGespreksAanvraagSchema = z.object({
  notitiesAdmin: z.string().trim().max(2000).optional(),
})
export type AfrondenGespreksAanvraagInput = z.infer<typeof AfrondenGespreksAanvraagSchema>

export const ListGespreksAanvragenQuerySchema = z.object({
  status: GespreksStatusEnum.optional(),
  type: GespreksTypeEnum.optional(),
  partner: GespreksPartnerEnum.optional(),
  medewerkerUserId: z.string().min(1).optional(),
})
export type ListGespreksAanvragenQuery = z.infer<typeof ListGespreksAanvragenQuerySchema>

// ─── DTO's ───────────────────────────────────────────────────────────────────

/**
 * Medewerker-view DTO — geen `notitiesAdmin` (interne admin-notitie).
 */
export interface GespreksAanvraagDTO {
  id: string
  medewerkerUserId: string
  type: GespreksType
  partner: GespreksPartner
  status: GespreksStatus
  toelichting: string | null
  geplandOp: string | null
  locatie: string | null
  afgerondOp: string | null
  /** Gekoppelde gesprekspartner (bv. specifieke VP). Null als niet gekozen. */
  partnerUserId: string | null
  createdAt: string
  updatedAt: string
}

/**
 * Backoffice-view DTO — incl. `notitiesAdmin` + medewerker-info.
 */
export interface GespreksAanvraagAdminDTO extends GespreksAanvraagDTO {
  notitiesAdmin: string | null
  medewerker: {
    id: string
    name: string | null
    email: string
  } | null
}

// ─── Labels (NL-talig voor UI) ───────────────────────────────────────────────

export const GESPREK_TYPE_LABELS: Record<GespreksType, string> = {
  PERSOONLIJK:         'Persoonlijk gesprek',
  SALARIS:             'Salarisbespreking',
  BEOORDELING:         'Beoordelingsgesprek',
  VERTROUWENSPERSOON:  'Vertrouwenspersoon / teamleider',
}

export const GESPREK_PARTNER_LABELS: Record<GespreksPartner, string> = {
  TEAMLEIDER:          'Teamleider',
  HR:                  'HR',
  DIRECTIE:            'Directie',
  VERTROUWENSPERSOON:  'Vertrouwenspersoon',
}

export const GESPREK_STATUS_LABELS: Record<GespreksStatus, string> = {
  AANGEVRAAGD:  'Aangevraagd',
  INGEPLAND:    'Ingepland',
  AFGEROND:     'Afgerond',
  GEANNULEERD:  'Geannuleerd',
}

/**
 * Badge-variant per status — gebruikt door portal + backoffice list-cards.
 * `warning` / `info` / `success` / `secondary` zijn de variants uit PR-A
 * (packages/ui/src/components/badge.tsx).
 */
export const GESPREK_STATUS_BADGE_VARIANT: Record<
  GespreksStatus,
  'warning' | 'info' | 'success' | 'secondary'
> = {
  AANGEVRAAGD:  'warning',
  INGEPLAND:    'info',
  AFGEROND:     'success',
  GEANNULEERD:  'secondary',
}
